/**
 * Notification API Slice — schema-contracted against
 * @gearghost/api-schemas/notification (tier B of the notifications
 * batch, 2026-07-21).
 *
 * The unread badge is DERIVED server-side (no stored count anywhere),
 * so read mutations only need to invalidate — a profile refetch is
 * always truthful.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import {
  NOTIFICATION_ROUTES,
  NotificationListEnvelopeSchema,
  NotificationListSchema,
  unwrapNotificationList,
  NotificationPreferencesEnvelopeSchema,
  NotificationPreferencesSchema,
  unwrapNotificationPreferences,
  MarkAllReadEnvelopeSchema,
  MarkAllReadResultSchema,
  unwrapMarkAllRead,
  UnsubscribeLandingEnvelopeSchema,
  UnsubscribeLandingSchema,
  unwrapUnsubscribeLanding,
  UnsubscribeResultEnvelopeSchema,
  UnsubscribeResultSchema,
  unwrapUnsubscribeResult,
} from '@gearghost/api-schemas/notification';
import type {
  NotificationList,
  NotificationsQueryRequest,
  NotificationPreferences,
  MarkAllReadResult,
  UpdatePreferencesRequest,
  UnsubscribeLanding,
  UnsubscribeRequest,
  UnsubscribeResult,
  SetUnsubscribePreferenceRequest,
} from '@gearghost/api-schemas/notification';
import { MessageOnlyEnvelopeSchema } from '@gearghost/api-schemas/auth';
import type { MessageOnlyEnvelope } from '@gearghost/api-schemas/auth';
import axiosBaseQuery from './axiosBaseQuery';
import { onSchemaFailure } from './onSchemaFailure';

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ['Notifications', 'NotificationPreferences', 'UnsubscribeLanding'],
  endpoints: (builder) => ({
    /**
     * Paginated notification list. The full payload flows through —
     * pageSize is the server's pagination contract (the trailing-empty-
     * pages fix); the v1 `type` filter is gone (clients scope their
     * Vendor/Renter view on each row's recipientRole).
     */
    // The query is the shared schema's (page, how many a page shows, category, status); the reply reports the page size served.
    getNotifications: builder.query<NotificationList, Partial<NotificationsQueryRequest>>({
      query: ({ page = 1, limit, category, status } = {}) => ({
        url: NOTIFICATION_ROUTES.list,
        params: {
          page,
          ...(limit === undefined ? {} : { limit }),
          ...(category ? { category } : {}),
          ...(status ? { status } : {}),
        },
      }),
      rawResponseSchema: NotificationListEnvelopeSchema,
      transformResponse: unwrapNotificationList,
      responseSchema: NotificationListSchema,
      providesTags: (_, __, { page, category } = {}) => [
        { type: 'Notifications', id: `${category || 'all'}-${page || 1}` },
        { type: 'Notifications', id: 'LIST' },
      ],
    }),

    // Mark a single notification as read (the unread badge is derived
    // server-side — no client-side count bookkeeping).
    markAsRead: builder.mutation<MessageOnlyEnvelope, number>({
      query: (id) => ({
        url: NOTIFICATION_ROUTES.markRead(id),
        method: 'PATCH',
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    // Mark all notifications as read.
    markAllAsRead: builder.mutation<MarkAllReadResult, void>({
      query: () => ({
        url: NOTIFICATION_ROUTES.readAll,
        method: 'PATCH',
      }),
      rawResponseSchema: MarkAllReadEnvelopeSchema,
      transformResponse: unwrapMarkAllRead,
      responseSchema: MarkAllReadResultSchema,
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    // Archive a notification.
    archiveNotification: builder.mutation<MessageOnlyEnvelope, number>({
      query: (id) => ({
        url: NOTIFICATION_ROUTES.archive(id),
        method: 'PATCH',
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    // Channel matrix: registry rows (server-filtered to the user's
    // roles) merged with their overrides. Rows keep the shared wire
    // shape — channels nested.
    getPreferences: builder.query<NotificationPreferences, void>({
      query: () => NOTIFICATION_ROUTES.preferences,
      rawResponseSchema: NotificationPreferencesEnvelopeSchema,
      transformResponse: unwrapNotificationPreferences,
      responseSchema: NotificationPreferencesSchema,
      providesTags: ['NotificationPreferences'],
    }),

    // Bulk update — full per-event state on every save (the shared
    // UpdatePreferencesRequestSchema is the server-side contract).
    updatePreferences: builder.mutation<
      MessageOnlyEnvelope,
      UpdatePreferencesRequest['preferences']
    >({
      query: (preferences) => ({
        url: NOTIFICATION_ROUTES.preferences,
        method: 'PUT',
        body: { preferences },
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
      invalidatesTags: ['NotificationPreferences'],
    }),

    // ── Public unsubscribe surface (GG-493) ─────────────────────────────
    // Token-authed, no session: the signed token from the email link is
    // the whole authorization. The landing query names the event the
    // token targets (so the page can say what its button will do) plus
    // the account's email-flag list; the two mutations flip flags and
    // invalidate the landing so the list refetches truthfully.

    getUnsubscribeLanding: builder.query<UnsubscribeLanding, string>({
      query: (token) =>
        `${NOTIFICATION_ROUTES.unsubscribePreferences}?token=${encodeURIComponent(token)}`,
      rawResponseSchema: UnsubscribeLandingEnvelopeSchema,
      transformResponse: unwrapUnsubscribeLanding,
      responseSchema: UnsubscribeLandingSchema,
      providesTags: ['UnsubscribeLanding'],
    }),

    unsubscribeFromEmail: builder.mutation<UnsubscribeResult, UnsubscribeRequest>({
      query: (body) => ({
        url: NOTIFICATION_ROUTES.unsubscribe,
        method: 'POST',
        body,
      }),
      rawResponseSchema: UnsubscribeResultEnvelopeSchema,
      transformResponse: unwrapUnsubscribeResult,
      responseSchema: UnsubscribeResultSchema,
      invalidatesTags: ['UnsubscribeLanding', 'NotificationPreferences'],
    }),

    setUnsubscribePreference: builder.mutation<
      UnsubscribeResult,
      SetUnsubscribePreferenceRequest
    >({
      query: (body) => ({
        url: NOTIFICATION_ROUTES.unsubscribePreferences,
        method: 'PUT',
        body,
      }),
      rawResponseSchema: UnsubscribeResultEnvelopeSchema,
      transformResponse: unwrapUnsubscribeResult,
      responseSchema: UnsubscribeResultSchema,
      invalidatesTags: ['UnsubscribeLanding', 'NotificationPreferences'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useArchiveNotificationMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useGetUnsubscribeLandingQuery,
  useUnsubscribeFromEmailMutation,
  useSetUnsubscribePreferenceMutation,
} = notificationApi;
